import React, { useCallback, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useStoreValue } from 'store';
import { LayoutPage, Form, LayoutContent, ButtonToggle, Address, Errors, SaveAndCompleteLaterButton } from 'components';
import { Container, Text, RadioTile, Flex, Callout, Link, List, Stack, Grid, Button } from 'basis';
import PremiereCardSvg from 'images/dj-premiere-card.svg';
import PrestigeCardSvg from 'images/dj-prestige-card.svg';
import TwentyEightDegreesCardACH from 'images/au-28d-ach.webp';
import GoCardACH from 'images/go-ach.webp';
import GemAuCardACH from 'images/gem-au-ach.webp';
import LowRateCardACH from 'images/low-rate-ach.webp';
import GemNzCardACH from 'images/gem-nz-ach.webp';
import { byCard, byCountry, isAU, isNZ, isNzV2, submitHandler } from 'utils';
import { useEventTracking } from 'react-event-tracker';
import { getDataLayerElements } from 'utils/getDataLayerElements';
import AchDetails from 'ach-details';
import {
  config,
  AdditionalCardHolderLinks,
  PRODUCT_CODE_DAVID_JONES_PRESTIGE,
  PRODUCT_CODE_DAVID_JONES_PREMIERE,
  PRODUCT_CODE_TWENTY_EIGHT_DEGREES,
  PRODUCT_CODE_GO,
  PRODUCT_CODE_AU_GEM,
  PRODUCT_CODE_LOW_RATE,
  PRODUCT_CODE_NZ_GEM,
} from '_config';
import styled from '@emotion/styled';
import { PrivacyNoticeModal as DjPrivacyNoticeModal } from 'views/landing-page-dj/PrivacyNoticeModal';
import { PrivacyNoticeModal as AuPrivacyNoticeModal } from 'views/landing-page-au/PrivacyNoticeModal';
import { getRatesAndFees } from 'featureToggles';
import {
  useFormInitialValues,
  useErrorTrackingAndUpdateStore,
  useDataLayerAndAnalyticsLinks,
  useDeliverApplication,
} from '../../hooks';
import { useSteps } from '../../hooks/useSteps';

const FORM_ID = 'achDetails';

const CardImageArea = styled.div`
  display: flex;
  background: ${props => props.theme.colors.specialBackground};
`;

const CardImageContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin: 24px auto;
  #dj-card {
    height: ${props =>
      byCard({
        [PRODUCT_CODE_TWENTY_EIGHT_DEGREES]: '238px',
        default: '150px',
      })};
  }
  #wallet-icon {
    margin-right: 8px;
  }
`;

const StyledList = styled.div`
  li::before {
    background-color: ${props => props.theme.colors.indigo};
    width: 0.3em;
    height: 0.3em;
  }
  li:not(:first-of-type) {
    margin-top: 2px;
  }

  li {
    color: ${props => props.theme.colors.indigo};
  }
`;

export const AdditionalCardHolder = () => {
  const history = useHistory();
  const [storeState, updateStore] = useStoreValue();
  const { acquisition, ...otherState } = storeState;
  const { trackEvent } = useEventTracking();
  const initialValues = useFormInitialValues(FORM_ID);
  const { handleErrorCallback } = useErrorTrackingAndUpdateStore('Next');
  const { nextStep, pathname, isLastDraftStep } = useSteps();
  const { trackEventSender } = useDataLayerAndAnalyticsLinks(storeState);
  const [privacyNoticeModalOpen, setPrivacyNoticeModalOpen] = useState(false);
  const submitButtonLabel = 'Next';

  const openPrivacyNoticeModal = () => {
    setPrivacyNoticeModalOpen(true);
    trackEventSender({
      action: 'interaction',
      location: 'additional-card-holder',
      label: 'Privacy Notice',
    });
  };

  const closePrivacyNoticeModal = () => {
    setPrivacyNoticeModalOpen(false);
  };

  const goToNextPage = useCallback(() => {
    trackEvent({
      event: {
        category: 'application',
        action: isLastDraftStep ? 'application-submitted' : 'application-navigation',
        location: 'additional-card-holder',
        label: isNZ() ? 'Next' : submitButtonLabel,
      },
      ...getDataLayerElements(storeState),
    });
    updateStore({
      activeStep: nextStep,
      applicationSubmitted: isLastDraftStep,
    });
    history.push(nextStep);
  }, [trackEvent, isLastDraftStep, submitButtonLabel, storeState, updateStore, nextStep, history]);

  const clearErrorAndGoNext = () => {
    updateStore({
      applicationErrors: null,
      applicationSubmitted: true,
    });
    goToNextPage();
  };

  const { deliver, loading } = useDeliverApplication({
    storeState,
    onSuccess: goToNextPage,
    onError: handleErrorCallback,
  });

  const { deliver: deliverAndSubmit, loading: loadingForSaveAndSubmit } = useDeliverApplication({
    storeState,
    onError: handleErrorCallback,
    onSuccess: clearErrorAndGoNext,
  });

  const onSubmit = useCallback(
    ({ values, errors }) => {
      const val = values;
      if (values.sameCurrentAddress === 'yes') {
        val.currentAddress = null;
      }

      const newAcquisition = {
        ...acquisition,
        achDetails: { ...val },
      };

      const updateStoreFormValues = () => {
        updateStore({
          acquisition: newAcquisition,
        });
      };

      submitHandler({
        submit: () => {
          updateStoreFormValues();
          byCountry({
            AU: () => {
              deliverAndSubmit(newAcquisition);
            },
            NZ: () => {
              if (isLastDraftStep) {
                deliver(newAcquisition);
              } else {
                goToNextPage();
              }
            },
          })();
        },
        errors,
      });
    },
    [acquisition, deliver, goToNextPage, isLastDraftStep, deliverAndSubmit, updateStore],
  );

  const retrySubmit = () => {
    trackEvent({
      event: {
        category: 'application',
        action: 'application-submitted',
        location: pathname,
        label: 'Retry',
      },
    });

    onSubmit({ values: storeState.acquisition.achDetails });
  };

  if (otherState.applicationErrors) {
    return (
      <LayoutPage>
        <Errors
          applicationRef={otherState.applicationRef}
          retry={retrySubmit}
          retrying={loadingForSaveAndSubmit || loading}
          isPreSubmission
        />
      </LayoutPage>
    );
  }

  const PrivacyNoticeModal = byCard({
    [PRODUCT_CODE_DAVID_JONES_PREMIERE]: DjPrivacyNoticeModal,
    [PRODUCT_CODE_DAVID_JONES_PRESTIGE]: DjPrivacyNoticeModal,
    default: AuPrivacyNoticeModal,
  });

  return (
    <LayoutPage>
      <Form
        id={FORM_ID}
        initialValues={initialValues}
        onSubmit={onSubmit}
        showSaveDraftButton
        saveDraftOnSubmit={!isLastDraftStep}
        saveDraftOnSessionTimeout
        hideFormButtons
      >
        {formData => {
          return (
            <LayoutContent rowsGap="6">
              <Container>
                <Text as="p">
                  An additional cardholder can make purchases and cash advances, request a replacement PIN and report
                  their card lost or stolen.
                </Text>
              </Container>
              <Container>
                <Text as="p">
                  They can't change the credit limit, request a balance transfer, close the account or alter the{' '}
                  address.
                </Text>
              </Container>
              {!isNZ() && (
                <Container>
                  <Text as="p">
                    You, as the primary cardholder, are financially responsible for all additional card use, making{' '}
                    payments and any outstanding balance on the account. You can also cancel the additional card at any{' '}
                    time.
                  </Text>
                </Container>
              )}
              <CardImageArea>
                <CardImageContainer>
                  <img
                    id="dj-card"
                    src={byCard({
                      [PRODUCT_CODE_DAVID_JONES_PREMIERE]: PremiereCardSvg,
                      [PRODUCT_CODE_DAVID_JONES_PRESTIGE]: PrestigeCardSvg,
                      [PRODUCT_CODE_TWENTY_EIGHT_DEGREES]: TwentyEightDegreesCardACH,
                      [PRODUCT_CODE_GO]: GoCardACH,
                      [PRODUCT_CODE_AU_GEM]: GemAuCardACH,
                      [PRODUCT_CODE_LOW_RATE]: LowRateCardACH,
                      [PRODUCT_CODE_NZ_GEM]: GemNzCardACH,
                      default: '',
                    })}
                    alt="Latitude credit card"
                  />
                  <Container margin="4 0 0 0">
                    <Flex>
                      <AdditionalCardFee additionalCardHolderFee={getRatesAndFees()?.additionalCardHolderFee} />
                    </Flex>
                  </Container>
                </CardImageContainer>
              </CardImageArea>
              <Text as="h4" textStyle="heading6">
                Do you want to add another cardholder to this account?
              </Text>
              <ButtonToggle
                name="hasAch"
                options={[
                  {
                    label: 'No',
                    value: 'no',
                  },
                  {
                    label: 'Yes',
                    value: 'yes',
                  },
                ]}
                testId="has-ach-toggle"
                validate={value => {
                  if (value === null) {
                    return 'Please select one option';
                  }
                  return null;
                }}
              />
              <Text>You can also add cardholder(s) later in the Latitude app or website</Text>

              {formData.state.values.hasAch === 'yes' && (
                <>
                  <Text as="h4" textStyle="heading5">
                    Please tell us about the additional cardholder
                  </Text>
                  {!isNzV2() && (
                    <Callout>
                      <Stack gap="3">
                        <Text color="indigo">
                          By providing us information related to the additional cardholder, you confirm that you have
                          been authorised by the additional cardholder to provide Latitude with their personal
                          information and have informed the additional cardholder of Latitude's{' '}
                          <Link onClick={openPrivacyNoticeModal}>Privacy Notice</Link> which contains important{' '}
                          information on how we manage their personal information and includes a link to our{' '}
                          <Link
                            newTab
                            href={AdditionalCardHolderLinks.privacy_policy.url}
                            onClick={() =>
                              trackEventSender({
                                action: 'external-link',
                                label: AdditionalCardHolderLinks.label,
                                location: 'additional-card-holder',
                              })
                            }
                          >
                            {AdditionalCardHolderLinks.privacy_policy.label}
                          </Link>
                          .
                        </Text>
                        <Text color="indigo">
                          The information you provide in relation to the additional cardholder will be used to verify
                          their identity and may include the use of external sources of information for this purpose.
                        </Text>
                        <Text color="indigo">Please provide the additional cardholders:</Text>
                        <StyledList>
                          <List>
                            <List.Item>full legal name (as shown on their ID)</List.Item>
                            <List.Item>date of birth</List.Item>
                            <List.Item>current residential address</List.Item>
                          </List>
                        </StyledList>
                      </Stack>
                    </Callout>
                  )}
                  <AchDetails
                    name="personalDetails"
                    countryCode={isAU() ? 'AU' : 'NZ'}
                    formData={formData}
                    testId="ach-details"
                    extraProps={{
                      ...(isNZ() && {
                        firstName: {
                          minLength: 2,
                          maxLength: 15,
                        },
                        middleName: {
                          minLength: 2,
                          maxLength: 15,
                        },
                        lastName: {
                          minLength: 2,
                          maxLength: 23,
                        },
                      }),
                      hasMiddleName: {
                        showCircles: true,
                        columns: 1,
                        color: 'white',
                      },
                    }}
                  />
                  <RadioTile
                    name="sameCurrentAddress"
                    label="Current residential address"
                    options={[
                      {
                        label: 'Address is the same as yours ',
                        value: 'yes',
                      },
                      {
                        label: 'No, additional cardholder lives at a different address ',
                        value: 'no',
                      },
                    ]}
                    color="white"
                    testId="same-current-address"
                  />
                  {formData.state.values.sameCurrentAddress === 'no' && (
                    <Address
                      name="currentAddress"
                      label="Current residential address"
                      testId="ach-current-address"
                      allowPropertyName={false}
                      allowPO={false}
                      countryCode={config.countryCode}
                      autoAddressErrorMessage="Please enter your current residential address."
                      autoAddressHelpText=""
                      formData={formData}
                    />
                  )}
                </>
              )}
              {isNzV2() && formData.state.values.hasAch === 'yes' && (
                <Stack gap="6">
                  <Text textStyle="heading5">Before you continue, please confirm:</Text>
                  <List>
                    <List.Item>
                      I am authorised by the additional cardholder to provide Latitude with their personal information
                      and have provided the additional cardholder with our{' '}
                      <Link
                        href="https://assets.latitudefinancial.com/gem-essentials/nz-gem-visa-privacy-notice.pdf"
                        newTab
                      >
                        Privacy Policy
                      </Link>{' '}
                      (which contains important information on how we manage their personal information), and that the
                      additional cardholder has read the contents of the Latitude Privacy Policy;
                    </List.Item>
                    <List.Item>
                      Use of the additional card will be in compliance with and subject to the terms of the credit
                      contract applicable to my account;
                    </List.Item>
                    <List.Item>
                      I, as the primary cardholder, am liable in all respects to Latitude for the use of the additional
                      card by the additional cardholder, and I acknowledge that I am financially responsible for all
                      amounts owing as a result of the use of the additional card use, for making payments by their due
                      dates and for any outstanding balance on the account. Also, I acknowledge that I can cancel the
                      additional card at any time;
                    </List.Item>
                    <List.Item>
                      The information provided in this application is true and correct and that no information which is
                      or could reasonably be relevant to Latitude either agreeing to issue the additional card or
                      otherwise granting credit to me has been withheld.
                    </List.Item>
                  </List>
                </Stack>
              )}
              <Buttons
                loading={loading}
                formData={formData}
                buttonLabel={
                  isAU()
                    ? submitButtonLabel
                    : formData.state.values.hasAch === 'yes'
                    ? 'Agree and continue'
                    : submitButtonLabel
                }
              />
            </LayoutContent>
          );
        }}
      </Form>
      <PrivacyNoticeModal isOpen={privacyNoticeModalOpen} onClose={closePrivacyNoticeModal} />
    </LayoutPage>
  );
};

function AdditionalCardFee({ additionalCardHolderFee }) {
  if (isNaN(additionalCardHolderFee) || additionalCardHolderFee === 0) {
    return (
      <Text>
        <strong>No additional charges or fees</strong>
      </Text>
    );
  }
  return (
    <Text>
      <strong>${additionalCardHolderFee} additional charges and fees</strong>
    </Text>
  );
}

function Buttons({ loading, buttonLabel, formData }) {
  return (
    <Grid rowsGap="4" colsGap="10" cols="2" margin="4 0 0 0">
      <Grid.Item colSpan="all" colSpan-sm="0" alignItems="center">
        <Grid>
          <Button type="submit" loading={loading} padding="0 2">
            {buttonLabel}
          </Button>
        </Grid>
      </Grid.Item>
      <Grid.Item colSpan="all" colSpan-sm="1" rowSpan-sm="0">
        <Grid>
          <SaveAndCompleteLaterButton formId={FORM_ID} formData={formData} isDrafting />
        </Grid>
      </Grid.Item>
    </Grid>
  );
}
